import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, X, Trash2 } from 'lucide-react';
import { useBrowsing } from '../context/BrowsingContext';
import { formatPrice, getSourceName } from '../lib/utils';
import { ListingQuickView } from './ListingQuickView';

const LAST_SEEN_KEY = 'scanmarket_recently_viewed_last_seen';

export function RecentlyViewedBar() {
  const { t } = useTranslation();
  const browsing = useBrowsing();
  const items = browsing?.recentlyViewed || [];
  const [expanded, setExpanded] = useState(false);
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    try { return localStorage.getItem(LAST_SEEN_KEY) || null; } catch { return null; }
  });

  // Conta quanti item sono stati aggiunti DOPO l'ultima apertura del pannello.
  // Se lastSeenAt è null (prima volta) → 0 (evita spam al primo ingresso).
  const newCount = useMemo(() => {
    if (!lastSeenAt || items.length === 0) return 0;
    return items.filter(i => i.viewed_at && i.viewed_at > lastSeenAt).length;
  }, [items, lastSeenAt]);

  // Quando l'utente apre il pannello, considera tutti gli item "già visti".
  useEffect(() => {
    if (expanded) {
      const now = new Date().toISOString();
      setLastSeenAt(now);
      try { localStorage.setItem(LAST_SEEN_KEY, now); } catch {}
    }
  }, [expanded]);

  // Bootstrap: se primo utilizzo e ci sono già item (ad es. importati da altra sessione),
  // fissa lastSeenAt a "now" per non mostrare subito il badge con dati vecchi.
  useEffect(() => {
    if (!lastSeenAt && items.length > 0) {
      const now = new Date().toISOString();
      setLastSeenAt(now);
      try { localStorage.setItem(LAST_SEEN_KEY, now); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0) return null;

  const handleRemove = (e, item) => {
    e.stopPropagation();
    browsing?.removeRecentlyViewed(item.source, item.source_listing_id);
  };

  const handleClearAll = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    browsing?.clearAllRecentlyViewed();
    setConfirmClear(false);
    setExpanded(false);
  };

  return (
    <>
      {/* Floating icon-only button (bottom-right). Posizione che segue lo
          scroll (position: fixed). Il label appare solo nel pannello aperto
          o come tooltip al hover. */}
      <button
        data-testid="recently-viewed-toggle"
        onClick={() => { setExpanded(!expanded); setConfirmClear(false); }}
        aria-label={t('recentlyViewed') || 'Visti di recente'}
        title={t('recentlyViewed') || 'Visti di recente'}
        className="fixed bottom-2 right-3 z-40 w-11 h-11 flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-transform"
        style={{
          background: 'var(--fab-bg)',
          color: 'var(--fab-fg)',
          border: '1px solid var(--fab-border)',
          forcedColorAdjust: 'none',
          WebkitForcedColorAdjust: 'none',
        }}
      >
        <Clock size={18} />
        {/* Count pill */}
        <span className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white text-[10px] font-bold flex items-center justify-center shadow border border-zinc-200 dark:border-zinc-700">
          {items.length}
        </span>
        {/* Badge "new": puntino rosso pulsante quando ci sono item aggiunti
            dopo l'ultima apertura del pannello. Nascosto quando expanded. */}
        {newCount > 0 && !expanded && (
          <span
            data-testid="recently-viewed-new-badge"
            className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 shadow-md ring-2 ring-background"
            aria-label={`${newCount} ${t('recentlyViewedNewBadge') || 'nuovi'}`}
          >
            <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60" />
            <span className="relative">{newCount > 9 ? '9+' : newCount}</span>
          </span>
        )}
      </button>

      {/* Expanded panel (ancorato a bottom-right) */}
      {expanded && (
        <div
          data-testid="recently-viewed-panel"
          className="fixed bottom-16 right-3 z-40 w-80 max-w-[calc(100vw-1.5rem)] max-h-[60vh] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-muted-foreground" />
              <span className="text-sm font-semibold">{t('recentlyViewed') || 'Visti di recente'}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="recently-viewed-clear-all"
                onClick={handleClearAll}
                className={`text-xs px-2 py-1 rounded-lg transition-colors ${confirmClear ? 'bg-red-500/15 text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/10'}`}
              >
                <span className="flex items-center gap-1">
                  <Trash2 size={12} />
                  {confirmClear ? (t('confirmClearAll') || 'Conferma?') : (t('clearAll') || 'Elimina tutto')}
                </span>
              </button>
              <button onClick={() => { setExpanded(false); setConfirmClear(false); }} className="text-muted-foreground hover:text-foreground ml-1">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-[calc(60vh-48px)] divide-y divide-border/30">
            {items.slice(0, 10).map((item, i) => (
              <div
                key={`${item.source}_${item.source_listing_id}_${i}`}
                className="flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group cursor-pointer relative"
                onClick={() => { setQuickViewItem(item); setExpanded(false); }}
                data-testid={`recently-viewed-item-${i}`}
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover bg-muted flex-shrink-0"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-muted flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-foreground transition-colors">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-sm font-semibold tabular-nums">
                      {item.price_chf != null ? formatPrice(item.price_chf, t) : ''}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {getSourceName(item.source)}
                    </span>
                  </div>
                </div>
                <button
                  data-testid={`recently-viewed-remove-${i}`}
                  onClick={(e) => handleRemove(e, item)}
                  className="flex-shrink-0 mt-1 p-1 rounded-md text-muted-foreground/40 hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QuickView */}
      {quickViewItem && (
        <ListingQuickView
          listing={quickViewItem}
          open={!!quickViewItem}
          onOpenChange={(open) => { if (!open) setQuickViewItem(null); }}
        />
      )}
    </>
  );
}
